import { LOCK_TYPE } from '../types';

import type { VisualizationAxis, VisualizationMeasurement } from './types';

export const ANGLE_MEASUREMENTS = {
    upper_torso_angular_position: {
        label: 'Shoulders Mid',
        type: 'measurement',
        axes: [
            'upper_torso_angular_position_tilt',
            'upper_torso_angular_position_bend',
            'upper_torso_angular_position_rotation',
        ],
    },
    pelvis_angular_position: {
        label: 'Pelvis',
        type: 'measurement',
        axes: ['pelvis_angular_position_tilt', 'pelvis_angular_position_bend', 'pelvis_angular_position_rotation'],
    },
    head_angular_position: {
        label: 'Head',
        type: 'measurement',
        axes: ['head_angular_position_tilt', 'head_angular_position_bend', 'head_angular_position_rotation'],
    },
    left_forearm_angle_with_club_shaft: {
        label: 'Lead forearm / club shaft',
        type: 'measurement',
        axes: ['left_forearm_angle_with_club_shaft_angle'],
    },
    spine_angle: {
        label: 'Spine angle',
        type: 'measurement',
        axes: ['spine_angle_angle'],
    },
    club_bend: {
        label: 'Club bend',
        type: 'measurement',
        axes: ['club_bend_shaft_lie_angle'],
    },
    club_tilt: {
        label: 'Club tilt',
        type: 'measurement',
        axes: ['club_tilt_shaft_lean_angle'],
    },
    shaft_direction_to_y_axis: {
        label: 'Shaft direction to Y-axis',
        type: 'measurement',
        axes: ['shaft_direction_to_y_axis_angle'],
    },
    club_angle_to_shaft_plane_at_setup: {
        label: 'Club angle to shaft plane at setup',
        type: 'measurement',
        axes: ['club_angle_to_shaft_plane_at_setup_angle'],
    },
    spine_bend: {
        label: 'Spine bend',
        type: 'measurement',
        axes: ['spine_bend_angle'],
    },
    spine_tilt: {
        label: 'Spine tilt',
        type: 'measurement',
        axes: ['spine_tilt_angle'],
    },
    x_factor: {
        label: 'X-factor',
        type: 'measurement',
        axes: ['x_factor_angle'],
    },
} as const satisfies Record<string, VisualizationMeasurement>;

export const ANGLE_MEASUREMENT_AXIS_PARENT_MAP = Object.fromEntries(
    Object.entries(ANGLE_MEASUREMENTS).flatMap(([measurementId, measurement]) =>
        measurement.axes.map((axisId) => [axisId, measurementId] as [string, string]),
    ),
);

export const ANGLE_AXES: Record<string, VisualizationAxis> = {
    upper_torso_angular_position_tilt: {
        id: 'upper_torso_angular_position_tilt',
        measurementName: 'upper_torso_angular_position',
        axisIndex: 0,
        label: 'Tilt',
        unit: 'rad',
        targetPoints: ['upper_torso', 'left_shoulder', 'right_shoulder'],
        camera: {
            type: LOCK_TYPE.INTRINSIC,
            axisIndex: 0,
            measurementName: 'upper_torso_angular_position',
        },
    },
    upper_torso_angular_position_bend: {
        id: 'upper_torso_angular_position_bend',
        measurementName: 'upper_torso_angular_position',
        axisIndex: 1,
        label: 'Bend',
        unit: 'rad',
        targetPoints: ['upper_torso', 'pelvis'],
        camera: {
            type: LOCK_TYPE.INTRINSIC,
            axisIndex: 1,
            measurementName: 'upper_torso_angular_position',
        },
    },
    upper_torso_angular_position_rotation: {
        id: 'upper_torso_angular_position_rotation',
        measurementName: 'upper_torso_angular_position',
        axisIndex: 2,
        label: 'Rotation',
        unit: 'rad',
        targetPoints: ['upper_torso', 'left_shoulder', 'right_shoulder'],
        camera: {
            type: LOCK_TYPE.INTRINSIC,
            axisIndex: 2,
            measurementName: 'upper_torso_angular_position',
        },
    },
    pelvis_angular_position_tilt: {
        id: 'pelvis_angular_position_tilt',
        measurementName: 'pelvis_angular_position',
        axisIndex: 0,
        label: 'Tilt',
        unit: 'rad',
        targetPoints: ['pelvis', 'left_hip', 'right_hip'],
        camera: { type: LOCK_TYPE.INTRINSIC, axisIndex: 0, measurementName: 'pelvis_angular_position' },
    },
    pelvis_angular_position_bend: {
        id: 'pelvis_angular_position_bend',
        measurementName: 'pelvis_angular_position',
        axisIndex: 1,
        label: 'Bend',
        unit: 'rad',
        targetPoints: ['pelvis', 'upper_torso'],
        camera: { type: LOCK_TYPE.INTRINSIC, axisIndex: 1, measurementName: 'pelvis_angular_position' },
    },
    pelvis_angular_position_rotation: {
        id: 'pelvis_angular_position_rotation',
        measurementName: 'pelvis_angular_position',
        axisIndex: 2,
        label: 'Rotation',
        unit: 'rad',
        targetPoints: ['pelvis', 'left_hip', 'right_hip'],
        camera: { type: LOCK_TYPE.INTRINSIC, axisIndex: 2, measurementName: 'pelvis_angular_position' },
    },
    head_angular_position_tilt: {
        id: 'head_angular_position_tilt',
        measurementName: 'head_angular_position',
        axisIndex: 0,
        label: 'Tilt',
        unit: 'rad',
        targetPoints: ['head', 'left_ear', 'right_ear'],
        camera: { type: LOCK_TYPE.INTRINSIC, axisIndex: 0, measurementName: 'head_angular_position' },
    },
    head_angular_position_bend: {
        id: 'head_angular_position_bend',
        measurementName: 'head_angular_position',
        axisIndex: 1,
        label: 'Bend',
        unit: 'rad',
        targetPoints: ['head', 'nose'],
        camera: { type: LOCK_TYPE.INTRINSIC, axisIndex: 1, measurementName: 'head_angular_position' },
    },
    head_angular_position_rotation: {
        id: 'head_angular_position_rotation',
        measurementName: 'head_angular_position',
        axisIndex: 2,
        label: 'Rotation',
        unit: 'rad',
        targetPoints: ['head', 'left_ear', 'right_ear'],
        camera: { type: LOCK_TYPE.INTRINSIC, axisIndex: 2, measurementName: 'head_angular_position' },
    },
    left_forearm_angle_with_club_shaft_angle: {
        id: 'left_forearm_angle_with_club_shaft_angle',
        measurementName: 'left_forearm_angle_with_club_shaft',
        axisIndex: 0,
        label: 'Angle',
        unit: 'rad',
        targetPoints: ['left_elbow', 'left_wrist'],

        // TODO: No directly equivalent camera exists in the current angle mappings.
        // The club shaft is stored in clubTrack rather than skeleton keypoints, so the
        // common static side-view fallback is used for scalar "other angle" measurements.
        camera: {
            type: LOCK_TYPE.STATIC,
            azimuthal: -Math.PI / 2,
            polar: Math.PI / 2,
        },
    },

    spine_angle_angle: {
        id: 'spine_angle_angle',
        measurementName: 'spine_angle',
        axisIndex: 0,
        label: 'Angle',
        unit: 'rad',
        targetPoints: ['upper_torso', 'pelvis'],

        // No existing intrinsic spine orientation measurement exists to copy.
        // Use the common static side-view fallback for scalar angles.
        camera: {
            type: LOCK_TYPE.STATIC,
            azimuthal: -Math.PI / 2,
            polar: Math.PI / 2,
        },
    },

    club_bend_shaft_lie_angle: {
        id: 'club_bend_shaft_lie_angle',
        measurementName: 'club_bend',
        axisIndex: 0,
        label: 'Shaft Lie angle',
        unit: 'rad',

        // TODO: Proxy skeleton points only. The real geometry belongs to clubTrack.
        targetPoints: ['left_elbow', 'left_wrist'],

        // No club equivalent exists in the current angle mapping group.
        // Keep scalar club angles on the same common side-view fallback.
        camera: {
            type: LOCK_TYPE.STATIC,
            azimuthal: -Math.PI / 2,
            polar: Math.PI / 2,
        },
    },

    club_tilt_shaft_lean_angle: {
        id: 'club_tilt_shaft_lean_angle',
        measurementName: 'club_tilt',
        axisIndex: 0,
        label: 'Shaft Lean angle',
        unit: 'rad',

        // TODO: Proxy skeleton points only. The real geometry belongs to clubTrack.
        targetPoints: ['left_elbow', 'left_wrist'],

        // Keep the same fallback as the other scalar club-angle mappings.
        camera: {
            type: LOCK_TYPE.STATIC,
            azimuthal: -Math.PI / 2,
            polar: Math.PI / 2,
        },
    },

    shaft_direction_to_y_axis_angle: {
        id: 'shaft_direction_to_y_axis_angle',
        measurementName: 'shaft_direction_to_y_axis',
        axisIndex: 0,
        label: 'Angle',
        unit: 'rad',

        // TODO: Proxy skeleton points only. The shaft itself belongs to clubTrack.
        targetPoints: ['left_elbow', 'left_wrist'],

        // Keep the same fallback as the other scalar club-angle mappings.
        camera: {
            type: LOCK_TYPE.STATIC,
            azimuthal: -Math.PI / 2,
            polar: Math.PI / 2,
        },
    },

    club_angle_to_shaft_plane_at_setup_angle: {
        id: 'club_angle_to_shaft_plane_at_setup_angle',
        measurementName: 'club_angle_to_shaft_plane_at_setup',
        axisIndex: 0,
        label: 'Angle',
        unit: 'rad',

        // TODO: Proxy skeleton points only. Ideally this would use clubTrack and
        // the shaft plane captured at setup.
        targetPoints: ['left_elbow', 'left_wrist'],

        // No shaft-plane camera lock currently exists, so copy the scalar
        // club-angle fallback used by this group.
        camera: {
            type: LOCK_TYPE.STATIC,
            azimuthal: -Math.PI / 2,
            polar: Math.PI / 2,
        },
    },

    spine_bend_angle: {
        id: 'spine_bend_angle',
        measurementName: 'spine_bend',
        axisIndex: 0,
        label: 'Angle',
        unit: 'rad',
        targetPoints: ['upper_torso', 'pelvis'],

        // Copy the existing Shoulder Mid Bend camera behavior.
        camera: {
            type: LOCK_TYPE.INTRINSIC,
            axisIndex: 1,
            measurementName: 'upper_torso_angular_position',
        },
    },

    spine_tilt_angle: {
        id: 'spine_tilt_angle',
        measurementName: 'spine_tilt',
        axisIndex: 0,
        label: 'Angle',
        unit: 'rad',
        targetPoints: ['upper_torso', 'pelvis'],

        // Copy the existing Shoulder Mid Tilt camera behavior.
        camera: {
            type: LOCK_TYPE.INTRINSIC,
            axisIndex: 0,
            measurementName: 'upper_torso_angular_position',
        },
    },

    x_factor_angle: {
        id: 'x_factor_angle',
        measurementName: 'x_factor',
        axisIndex: 0,
        label: 'Angle',
        unit: 'rad',
        targetPoints: ['upper_torso', 'left_shoulder', 'right_shoulder'],

        // X-factor is based on shoulder/pelvis rotation, so copy the existing
        // Shoulder Mid Rotation camera behavior.
        camera: {
            type: LOCK_TYPE.INTRINSIC,
            axisIndex: 2,
            measurementName: 'upper_torso_angular_position',
        },
    },
};
